/**
 * LandingPage — public marketing homepage (Notion-inspired)
 * Hero with cycling headline and a product mockup surrounded by floating
 * mascot characters, followed by one self-playing demo section per feature,
 * a closing CTA band, and the classroom crowd illustration.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../../components/shared/Logo';
import Mascot, { MascotName } from '../../components/shared/Mascot';
import { useTheme } from '../../context/ThemeContext';
import { UploadDemo, TutorDemo, InsightsDemo, DemoWindow } from './FeatureDemos';
import BloomLadderDemo from '../../components/shared/BloomLadderDemo';

// Overlapping row of blinking mascot chips above the headline (the same
// characters used across the page and in the logo).
const HERO_ROW: { name: MascotName; chip: string }[] = [
  { name: 'book', chip: '#fbbf24' },
  { name: 'bulb', chip: '#fb923c' },
  { name: 'apple', chip: '#f472b6' },
  { name: 'clock', chip: '#fde047' },
  { name: 'folder', chip: '#93c5fd' },
  { name: 'star', chip: '#a5b4fc' },
];

// Each cycling word carries its own color (Notion-style), independent of the theme.
const WORDS: { text: string; color: string }[] = [
  { text: 'learn', color: '#059669' },   // emerald
  { text: 'grow', color: '#2563eb' },    // blue
  { text: 'think', color: '#7c3aed' },   // violet
  { text: 'explore', color: '#ea580c' }, // orange
  { text: 'create', color: '#db2777' },  // pink
];

// Mascots floating around the hero mockup, Notion-homepage style.
const HERO_MASCOTS: { name: MascotName; chip: string; className: string; float: string; delay: number }[] = [
  { name: 'book', chip: '#fbbf24', className: '-left-6 top-6 h-16 w-16 sm:-left-14 sm:top-10 sm:h-20 sm:w-20', float: 'animate-float-slow', delay: 0 },
  { name: 'bulb', chip: '#fb923c', className: '-right-5 top-1/3 h-14 w-14 sm:-right-14 sm:h-20 sm:w-20', float: 'animate-float-medium', delay: 1.3 },
  { name: 'apple', chip: '#f472b6', className: '-right-3 -bottom-5 h-14 w-14 sm:-right-10 sm:-bottom-7 sm:h-18 sm:w-18', float: 'animate-float-fast', delay: 2.1 },
  { name: 'clock', chip: '#fde047', className: '-left-4 bottom-1/4 h-12 w-12 sm:-left-12 sm:h-16 sm:w-16', float: 'animate-float-medium', delay: 0.7 },
  { name: 'star', chip: '#a5b4fc', className: '-top-6 right-10 hidden h-14 w-14 sm:flex', float: 'animate-float-fast', delay: 1.8 },
];

// One entry per product feature; demos are self-playing looping mockups.
const FEATURES: {
  mascot: MascotName;
  chip: string;
  kicker: string;
  title: string;
  body: string;
  demo: React.ReactNode;
}[] = [
  {
    mascot: 'folder',
    chip: '#93c5fd',
    kicker: 'For teachers',
    title: 'Drop in a lecture, get a course',
    body: 'Upload a recording, slides, or a PDF. classrooms.work extracts the topics, builds quiz questions, and spins up an AI tutor that knows your material, all in minutes.',
    demo: <UploadDemo />,
  },
  {
    mascot: 'bulb',
    chip: '#fb923c',
    kicker: 'For students',
    title: 'A tutor that actually took the class',
    body: 'Students ask questions in plain language and get answers grounded in the exact lecture, with a pointer back to the moment it was taught. No wandering off to the whole internet.',
    demo: <TutorDemo />,
  },
  {
    mascot: 'book',
    chip: '#fbbf24',
    kicker: 'Practice',
    title: "Quizzes that climb Bloom's ladder",
    body: 'Our AI does not just write questions, it builds ladders. Every generated quiz climbs all six levels of Bloom\'s taxonomy, from Remember to Create, and each question builds on the one before it, so practice deepens instead of repeating.',
    demo: <BloomLadderDemo />,
  },
  {
    mascot: 'star',
    chip: '#a5b4fc',
    kicker: 'Insights',
    title: 'Find the hard parts before the exam does',
    body: 'Every class has topics that quietly lose half the room. classrooms.work surfaces exactly which topics your students are struggling with, who is stuck, and at which level of understanding, so you can reteach the right thing at the right time.',
    demo: <InsightsDemo />,
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [wordIdx, setWordIdx] = useState(0);
  // Bottom crowd illustration auto-hides until the image file exists.
  const [showCrowd, setShowCrowd] = useState(true);

  useEffect(() => {
    const id = setInterval(() => setWordIdx((i) => (i + 1) % WORDS.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-white dark:bg-secondary-900">
      {/* Nav */}
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-5">
        <Logo size="md" onClick={() => navigate('/')} />
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-secondary-800"
          >
            {isDark ? (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-secondary-800"
          >
            Log in
          </button>
          <button
            onClick={() => navigate('/register')}
            className="rounded-full bg-primary-600 px-5 py-2 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            Get started
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center px-6 pt-12 text-center sm:pt-16">
        {/* Connected mascot row */}
        <div className="flex items-center">
          {HERO_ROW.map((m, i) => (
            <div
              key={m.name}
              className={`${i > 0 ? '-ml-3' : ''} ${i % 3 === 0 ? 'animate-float-slow' : i % 3 === 1 ? 'animate-float-medium' : 'animate-float-fast'}`}
              style={{ zIndex: i }}
            >
              <Mascot
                name={m.name}
                chip={m.chip}
                className="h-16 w-16 ring-4 ring-white dark:ring-secondary-900"
                blinkDelay={i * 0.7}
              />
            </div>
          ))}
        </div>

        {/* Animated headline */}
        <h1 className="mt-10 text-4xl font-extrabold leading-[1.15] tracking-tight text-gray-900 dark:text-white sm:text-6xl">
          Where students and AI
          <span className="mt-2 block">
            <span
              className="mr-2 inline-block rounded-2xl px-3 py-1 align-baseline transition-colors duration-300"
              style={{ backgroundColor: `${WORDS[wordIdx].color}22` }}
            >
              <span key={wordIdx} className="animate-fade-in-up inline-block" style={{ color: WORDS[wordIdx].color }}>
                {WORDS[wordIdx].text}
              </span>
            </span>
            together.
          </span>
        </h1>

        <p className="mt-6 max-w-xl text-lg text-gray-500 dark:text-gray-400">
          Turn any lecture into an interactive AI tutor, instant quizzes, and study
          tools, built for your classroom.
        </p>

        {/* CTAs */}
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <button
            onClick={() => navigate('/register')}
            className="rounded-full bg-primary-600 px-7 py-3 text-base font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
          >
            Get started free
          </button>
          <button
            onClick={() => navigate('/login')}
            className="rounded-full border-2 border-gray-200 px-7 py-3 text-base font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-700 dark:text-gray-200 dark:hover:bg-secondary-800"
          >
            Log in
          </button>
        </div>

        {/* Hero product mockup with floating mascots */}
        <div className="relative mt-16 w-full max-w-3xl sm:mt-20">
          {HERO_MASCOTS.map((m) => (
            <div key={m.name} className={`absolute z-10 ${m.className} ${m.float}`}>
              <Mascot name={m.name} chip={m.chip} className="h-full w-full shadow-medium" blinkDelay={m.delay} />
            </div>
          ))}
          <DemoWindow title="classrooms.work / Biology 101" className="text-left">
            <div className="flex gap-4">
              {/* Mini sidebar */}
              <div className="hidden w-36 flex-shrink-0 flex-col gap-1.5 sm:flex">
                <p className="px-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">My courses</p>
                {['🧬 Biology 101', '📐 Calculus II', '🌍 World History'].map((c, i) => (
                  <span
                    key={c}
                    className={`rounded-lg px-2 py-1.5 text-xs font-semibold ${
                      i === 0
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {c}
                  </span>
                ))}
              </div>
              {/* Mini course view */}
              <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                <p className="text-sm font-extrabold text-gray-800 dark:text-gray-100">Biology 101</p>
                {[
                  { icon: '🎬', name: 'Week 4 - Photosynthesis', meta: '4 topics · 24 questions' },
                  { icon: '🎬', name: 'Week 5 - Cell Respiration', meta: '5 topics · 30 questions' },
                ].map((l) => (
                  <div
                    key={l.name}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-secondary-600"
                  >
                    <span className="text-base">{l.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-gray-700 dark:text-gray-200">{l.name}</p>
                      <p className="text-[11px] text-gray-500">{l.meta}</p>
                    </div>
                    <span className="rounded-full bg-primary-600 px-2.5 py-1 text-[11px] font-bold text-gray-900">
                      Study
                    </span>
                  </div>
                ))}
                <div className="mt-1 flex items-center gap-2 rounded-full border border-gray-200 px-3.5 py-2 dark:border-secondary-600">
                  <span className="flex-1 truncate text-xs text-gray-500">Ask the tutor anything...</span>
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-[11px] text-gray-900">
                    ↑
                  </span>
                </div>
              </div>
            </div>
          </DemoWindow>
        </div>
      </main>

      {/* Feature sections */}
      <section className="mx-auto mt-24 flex w-full max-w-6xl flex-col gap-24 px-6 sm:mt-32 sm:gap-32">
        {FEATURES.map((f, i) => (
          <div
            key={f.title}
            className={`flex flex-col items-center gap-10 lg:gap-16 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
          >
            {/* Copy */}
            <div className="flex w-full flex-col items-start lg:w-[42%]">
              <Mascot name={f.mascot} chip={f.chip} className="h-16 w-16 shadow-medium" blinkDelay={i * 0.9} />
              <p className="mt-5 text-sm font-bold uppercase tracking-wide text-primary-600">{f.kicker}</p>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                {f.title}
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-500 dark:text-gray-400">{f.body}</p>
            </div>
            {/* Self-playing demo */}
            <div className="w-full lg:w-[58%]">{f.demo}</div>
          </div>
        ))}
      </section>

      {/* Closing CTA */}
      <section className="mx-auto mt-28 w-full max-w-4xl px-6 text-center sm:mt-36">
        <div className="flex justify-center gap-3">
          <Mascot name="book" chip="#fbbf24" className="h-14 w-14 shadow-medium animate-float-slow" blinkDelay={0.4} />
          <Mascot name="apple" chip="#f472b6" className="h-14 w-14 shadow-medium animate-float-medium" blinkDelay={1.5} />
          <Mascot name="bulb" chip="#fb923c" className="h-14 w-14 shadow-medium animate-float-fast" blinkDelay={2.6} />
        </div>
        <h2 className="mt-8 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
          Your next lecture could teach itself.
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-lg text-gray-500 dark:text-gray-400">
          Upload one lecture and see what your classroom looks like with an AI tutor in it.
        </p>
        <button
          onClick={() => navigate('/register')}
          className="mt-8 rounded-full bg-primary-600 px-8 py-3.5 text-base font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
        >
          Get started free
        </button>
      </section>

      {/* Full-bleed classroom crowd at the bottom (Open Peeps). Drop the image at
          public/classroom-crowd.png (or .svg). Auto-hidden until the file exists. */}
      {showCrowd && (
        <div className="relative mt-16 w-full">
          {/* Fade so the crowd emerges softly from the page */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-28 bg-gradient-to-b from-white to-transparent dark:from-secondary-900" />
          <img
            src="/classroom-crowd.png"
            alt="A diverse crowd of students"
            onError={() => setShowCrowd(false)}
            draggable={false}
            className="w-full select-none object-cover object-top"
            style={{ maxHeight: '46vh' }}
          />
        </div>
      )}

      <footer className="py-8 text-center text-sm text-gray-500 dark:text-gray-600">
        © {new Date().getFullYear()} classrooms.work
      </footer>
    </div>
  );
};

export default LandingPage;
